import { Injectable } from '@angular/core';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { TranslateService } from '@ngx-translate/core';
import { IReportData } from '../../../models/reports/IReportData';
import { IProjectHierarchyItem, Material, MaterialData, MaterialLevelwiseData, SQWebArea, SQWebSystem, SubSystem, SystemComponent, TypicalOfSystemHelper } from '../../../models/dtos';
import { ITableData } from '../../../models/reports/ITableData';
import { IndexDBService } from '../../common/indexDB.service';
import { IData } from '../../../models/reports/IData';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';

@Injectable()
export class SolSystemsReportService {
  private projectHierarchyItems: Record<string, IProjectHierarchyItem> = {};
  private typicalOfHelper: Record<string, TypicalOfSystemHelper> = {};
  private processedTypicalIds: Map<string, number> = new Map();

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly indexDBService: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,

  ) { }

  public async generateReport(
    reportId: ReportList,
    pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true
  ): Promise<void> {
    this.projectHierarchyItems = this.commonDataService.getProjectHierarchyItems();
    if (!this.projectHierarchyItems || Object.keys(this.projectHierarchyItems).length === CONSTANTS.ZERO) {
      this.projectHierarchyItems = this.buildHierarchyItemsFromProject();
    }
    this.typicalOfHelper = this.commonDataService.getTypicalOfHelpers();
    this.processedTypicalIds.clear();
    const tableData = await this.generateTableData(reportId, pageOrientation);
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: tableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };
    
    this.reportsCommonService.setSelectedReport(reportData);
  }

  private async generateTableData(reportId: ReportList, pageOrientation: ReportPageOrientation): Promise<ITableData[]> {
    switch (reportId) {
      case ReportList.SystemsReportViewModel:
        return this.generateSystemData(pageOrientation);
      case ReportList.SystemNotesViewModel:
        return this.generateSystemNotesData();
      default:
        return [];
    }
  }

  private buildHierarchyItemsFromProject(): Record<string, IProjectHierarchyItem> {
    const hierarchyItems: Record<string, IProjectHierarchyItem> = {};
    const sqWebProject = this.commonDataService.getSQWebProject();
    const areas = sqWebProject?.areas ?? [];

    const addAreaSystems = (area: SQWebArea | undefined): void => {
      if (!area?.children) {
        return;
      }

      for (const child of area.children) {
        const childItem = child as SQWebArea | SQWebSystem;
        if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
          addAreaSystems(childItem as SQWebArea);
        } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = childItem as SQWebSystem;
          if (system.itemUniqueId && system.isItemVisible) {
            hierarchyItems[system.itemUniqueId] = system;
          }
        }
      }
    };

    for (const area of areas) {
      if (area?.isItemVisible) {
        addAreaSystems(area);
      }
    }

    return hierarchyItems;
  }

  //#region Systems Report Methods

  private async generateSystemData(pageOrientation: ReportPageOrientation): Promise<ITableData[]> {
    const allMaterialData: MaterialLevelwiseData = await this.indexDBService.getAllMaterialData();
    const materialsCollection = allMaterialData.materialsCollection;

    if (!materialsCollection || Object.keys(materialsCollection).length === 0) {
      return [];
    }

    const sqWebProject = this.commonDataService.getSQWebProject();
    const projectAreas = sqWebProject?.areas ?? [];
    if (projectAreas.length === 0) {
      return [];
    }

    const data: Record<string, MaterialData> = {};

    for (const area of projectAreas) {
      this.traverseProjectAreaForSystems(area, materialsCollection, data);
    }

    if (Object.keys(data).length === 0) {
      return [];
    }

    return this.getMaterialFilteredDataForSystem(data, pageOrientation);
  }

  private traverseProjectAreaForSystems(
    area: SQWebArea,
    materialsCollection: Record<string, MaterialData>,
    data: Record<string, MaterialData>
  ): void {
    if (!area.children || !materialsCollection) {
      return;
    }

    for (const child of area.children) {
      const childObj = child as SQWebArea | SQWebSystem;

      if (childObj.nodeType === CONSTANTS.NODETYPE_AREA) {
        this.traverseProjectAreaForSystems(childObj as SQWebArea, materialsCollection, data);
      } else {
        this.processSystemNode(childObj as SQWebSystem, materialsCollection, data);
      }
    }
  }

  private handleTypicalOf(system: SQWebSystem): boolean {
    const typicalId = system.systemTypicalOfHelperUniqueId;
    if (!typicalId) {
      return false;
    }

    const typicalOf = this.typicalOfHelper[typicalId];
    if (!typicalOf) {
      return false;
    }
    if (this.processedTypicalIds.has(typicalId)) {
      return true;
    }

    const typicalOfCount = typicalOf.typicalOfSystems?.length ?? CONSTANTS.ONE;
    this.processedTypicalIds.set(typicalId, typicalOfCount);
    return false;
  }

  private processSystemNode(
    node: SQWebSystem,
    materialsCollection: Record<string, MaterialData>,
    data: Record<string, MaterialData>
  ): void {
    if (!node.itemUniqueId || node.nodeType !== CONSTANTS.NODETYPE_BSETSYSTEM || !node.isItemVisible) {
      return;
    }
    if (this.handleTypicalOf(node)) {
      return;
    }

    const typicalId = node.systemTypicalOfHelperUniqueId;
    const typicalOf = typicalId ? this.typicalOfHelper[typicalId] : undefined;
    const materialId = typicalOf?.typicalOfCollectionHolderId ?? node.itemUniqueId;
    const mat = materialsCollection[materialId];
    if (mat) {
      data[node.itemUniqueId] = mat;
    }
  }

  private addSystemRow(
    component: SystemComponent,
    systemRows: IData[],
    hierarchyLevel: number
  ): void {
    // Master components carry the quantity; non-master instances must not repeat that row.
    if (!component.isMaster || component.quantity <= CONSTANTS.ZERO) {
      return;
    }

    const material = component as Material;
    const source = component.nodeType === CONSTANTS.NODETYPE_SUBSYSTEM
      ? 'KIT'
      : (material.classification ?? CONSTANTS.EMPTY_STRING).trim() || 'SOC';

    const row = this.createRowForValueData(
      component.referenceNumber,
      component.description,
      component.quantity.toString(),
      source,
      this.IOArray(component),
      component.graphic === CONSTANTS.ONE ? CONSTANTS.YES : CONSTANTS.EMPTY_STRING,
      component.alarm === CONSTANTS.ONE ? CONSTANTS.YES : CONSTANTS.EMPTY_STRING,
      component.trend === CONSTANTS.ONE ? CONSTANTS.YES : CONSTANTS.EMPTY_STRING,
      hierarchyLevel
    );

    systemRows.push(row);
    if (component.nodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
      for (const child of (component as SubSystem).children ?? []) {
        this.addSystemRow(child, systemRows, hierarchyLevel + CONSTANTS.ONE);
      }
    }
  }

  private getMaterialFilteredDataForSystem(data: Record<string, MaterialData>, pageOrientation: ReportPageOrientation): ITableData[] {
    const tables: ITableData[] = [];

    for (const [key, materialData] of Object.entries(data)) {
      const hierarchyItem = this.projectHierarchyItems[key];
      if (!hierarchyItem) {
        continue;
      }

      const systemRows: IData[] = [];
      if (materialData.systemChildrenCollection) {
        for (const item of materialData.systemChildrenCollection) {
          this.addSystemRow(item, systemRows, CONSTANTS.ZERO);
        }
      }

      if (systemRows.length > CONSTANTS.ZERO) {
        tables.push(this.createSystemTable(hierarchyItem, systemRows, key, pageOrientation));
      }
    }

    return tables;
  }

  private createSystemTable(hierarchyItem: IProjectHierarchyItem, systemRows: IData[], key: string, pageOrientation: ReportPageOrientation): ITableData {
    const typicalId = (hierarchyItem as SQWebSystem).systemTypicalOfHelperUniqueId;
    const count = (typicalId ? this.processedTypicalIds.get(typicalId) : undefined) ?? CONSTANTS.ONE;
    const whereUsedText = this.translate.instant('WHERE_USED');
    const letterPageWidth = pageOrientation === CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT ? 612 : 792;
    const horizontalPageMargins = 40;
    const verticalBorderWidth = 9 * 0.5;
    const tableWidth = letterPageWidth - horizontalPageMargins - verticalBorderWidth;
    return {
      tableHeader: [{
        headers: [
          { headerText: hierarchyItem.referenceNumber, columnspan: CONSTANTS.ONE },
          { headerText: hierarchyItem.itemName, columnspan: CONSTANTS.ONE },
          { headerText: count.toString(), columnspan: CONSTANTS.ONE, alignment: CONSTANTS.CENTER_ALIGNMENT },
          { headerText: CONSTANTS.EMPTY_STRING, columnspan: CONSTANTS.TWO },
          { headerText: whereUsedText, columnspan: CONSTANTS.THREE, alignment: CONSTANTS.CENTER_ALIGNMENT }
        ]
      }],
      title: [
        { data: this.translate.instant('MATERIAL'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.17).toString() },
        { data: this.translate.instant('DESCRIPTION'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.285).toString() },
        { data: this.translate.instant('Quantity'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.05).toString() },
        { data: this.translate.instant('SOURCE'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.095).toString() },
        { data: this.translate.instant('IO'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.10).toString() },
        { data: this.translate.instant('GRAPHIC'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.10).toString() },
        { data: this.translate.instant('TREND'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.10).toString() },
        { data: this.translate.instant('ALARM'), isSubHeader: false, columnspan: 1, hierarchyLevel: 0, width: (tableWidth * 0.10).toString() }
      ],
      data: systemRows,
      tableFooter: [],
      isPageBreakAfter: false,
      isTableBordered: true
    };
  }

  private createRowForValueData(
    referenceNumber: string,
    description: string,
    qty: string,
    classification: string,
    io: string,
    graphic: string,
    alarm: string,
    trend: string,
    hierarchyLevel: number
  ): IData {
    return {
      data: [
        { data: referenceNumber, isSubHeader: false, columnspan: 1, hierarchyLevel, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: description, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT },
        { data: qty, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: classification, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: io, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: graphic, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: trend, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT },
        { data: alarm, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.CENTER_ALIGNMENT }
      ]
    };
  }

  private IOArray(children: SystemComponent): string {
    if (children.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
      return CONSTANTS.EMPTY_STRING;
    }

    const ioArr: string[] = [];
    
    if (children.analogInput > CONSTANTS.ZERO) {
      ioArr.push(children.analogInput > CONSTANTS.ONE ? `AI(${children.analogInput})` : 'AI');
    }
    if (children.analogOutput > CONSTANTS.ZERO) {
      ioArr.push(children.analogOutput > CONSTANTS.ONE ? `AO(${children.analogOutput})` : 'AO');
    }
    if (children.digitalInput > CONSTANTS.ZERO) {
      ioArr.push(children.digitalInput > CONSTANTS.ONE ? `DI(${children.digitalInput})` : 'DI');
    }
    if (children.digitalOutput > CONSTANTS.ZERO) {
      ioArr.push(children.digitalOutput > CONSTANTS.ONE ? `DO(${children.digitalOutput})` : 'DO');
    }
    if( children.analogVirtual > CONSTANTS.ZERO) {
      ioArr.push(children.analogVirtual > CONSTANTS.ONE ? `AV(${children.analogVirtual})` : 'AV');
    }
    if (children.digitalVirtual > CONSTANTS.ZERO) {
      ioArr.push(children.digitalVirtual > CONSTANTS.ONE ? `DV(${children.digitalVirtual})` : 'DV');
    }
    if (children.integration > CONSTANTS.ZERO) {
      ioArr.push(children.integration > CONSTANTS.ONE ? `INT(${children.integration})` : 'INT');
    }
    if(children.fln > CONSTANTS.ZERO) {
      ioArr.push(children.fln > CONSTANTS.ONE ? `FLN(${children.fln})` : 'FLN');
    }
    
    return ioArr.join(', ');
  }

  //#endregion

  //#region Notes Report Methods

  private async generateSystemNotesData(): Promise<ITableData[]> {
    return this.getMaterialFilteredDataForSystemNotes();
  }

  private getMaterialFilteredDataForSystemNotes(): ITableData[] {
    const tables: ITableData[] = [];

    for (const material of Object.values(this.projectHierarchyItems)) {
      const system = material as SQWebSystem;
      if (system.nodeType !== CONSTANTS.NODETYPE_BSETSYSTEM) {
        continue;
      }

      let systemTitle = system.itemName || CONSTANTS.EMPTY_STRING;
      const typicalId = system.systemTypicalOfHelperUniqueId;
      if (system.hasTypicalOf || typicalId) {
        const typicalOf = typicalId ? this.typicalOfHelper[typicalId] : undefined;
        const typicalOfCount = typicalOf?.typicalOfSystems?.length ?? CONSTANTS.ONE;
        systemTitle = `${CONSTANTS.TYPICAL_OF_LOGO} of ${typicalOfCount} ${systemTitle}`;
      }

      const hasInternalNotes = !!(system.internalNotes && system.internalNotes.trim());
      const hasExternalNotes = !!(system.externalNotes && system.externalNotes.trim());

      if (!hasInternalNotes && !hasExternalNotes) {
        continue;
      }

      const mainRows: IData[] = [];

      if (hasInternalNotes) {
        mainRows.push(
          { data: [{ data: CONSTANTS.INTERNAL_SYSTEM_NOTES, isSubHeader: true, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR }] },
          { data: [{ data: system.internalNotes, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }] }
        );

        if (hasExternalNotes) {
          mainRows.push(
            { data: [{ data: CONSTANTS.EMPTY_STRING, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }] }
          );
        }
      }

      if (hasExternalNotes) {
        mainRows.push(
          { data: [{ data: CONSTANTS.EXTERNAL_SYSTEM_NOTES, isSubHeader: true, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT, DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR }] },
          { data: [{ data: system.externalNotes, isSubHeader: false, columnspan: 1, hierarchyLevel: 0, alignment: CONSTANTS.LEFT_ALIGNMENT }] }
        );
      }

      tables.push({
        tableHeader: [],
        title: [{
          data: systemTitle,
          isSubHeader: true,
          columnspan: 1,
          hierarchyLevel: 0,
          alignment: CONSTANTS.LEFT_ALIGNMENT,
          width: '*',
          DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR
        }],
        data: mainRows,
        tableFooter: [],
        isPageBreakAfter: false,
        isTableBordered: true
      });
    }

    return tables;
  }

  //#endregion
}
